<script setup lang="ts">
import type { IDisplayedProgrammingRange } from "@/components/piloting/planning/section-programming-ranges-cell.types";
import type { ProgrammingRangeId } from "@/data/common/ids";
import type { ISystemColor } from "@/utils/style-utils";
import type { MenuItem } from "primevue/menuitem";

import { cn } from "@voltalis/frontend-utils";
import { ShieldIcon } from "@voltalis/ui-kit/icons";
import { useNow } from "@vueuse/core";
import { computed, ref, useTemplateRef } from "vue";

import {
  DEFAULT_PROGRAM_ID,
  isProgrammingRangePassed,
} from "@/components/piloting/planning/section-programming-ranges-utils";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { ProgramColorName } from "@/domains/v4b/program/program.types";
import { useTypedIntl } from "@/locales/typed-intl";
import $date from "@/utils/date";
import { mapProgramColorNameToSystemColor } from "@/utils/programs/program-utils";

import Menu, { type MenuPassThroughOptions } from "primevue/menu";

const props = defineProps<{
  programmingRangeId: ProgrammingRangeId;
  programmingRange: IDisplayedProgrammingRange;
  displayedStartDate: Date;
  displayedEndDate: Date;
  programColor: ProgramColorName;
}>();
const emits = defineEmits<{
  (e: "click:edit"): void;
  (e: "click:delete"): void;
}>();

const { t, k } = useTypedIntl();
const now = useNow({ interval: 1_000 });
const programmingRangeMenu = useTemplateRef("programmingRangeMenu");
const programmingRangeMenuId = `programming-range-menu-${props.programmingRangeId}`;
const isProgrammingRangeMenuVisible = ref(false);

const programmingRangeMenuPassThrough: MenuPassThroughOptions = {
  root: {
    class:
      "!min-w-[123px] !overflow-hidden !border !border-solid !border-neutral-light !rounded-xl !bg-white !p-0 !shadow-[0px_12px_16px_-4px_rgba(16,24,40,0.08),0px_4px_6px_-2px_rgba(16,24,40,0.03)]",
  },
  list: {
    class: "!gap-0 !p-2",
  },
  itemContent: {
    class: "!rounded-xl",
  },
  itemLink: {
    class: "!gap-1 !px-3 !py-2",
  },
  itemIcon: ({ context }) => ({
    class: `!mr-0 !size-[14px] !text-[14px] !leading-[14px] ${
      context.item.key === "delete" ? "!text-danger-strong" : "!text-text-secondary"
    }`,
  }),
  itemLabel: ({ context }) => ({
    class: `!text-xs !font-semibold !leading-none ${
      context.item.key === "delete" ? "!text-danger-strong" : "!text-text-primary"
    }`,
  }),
};

const programSystemColor = computed<ISystemColor>(() => {
  return mapProgramColorNameToSystemColor(props.programColor);
});

const isEditable = computed(() => !isProgrammingRangePassed(props.programmingRange, now.value));

const programmingRangeMenuItems = computed<MenuItem[]>(() => [
  {
    key: "edit",
    label: t(k.global.misc.edit),
    icon: "pi pi-pencil",
    command: () => emits("click:edit"),
  },
  {
    key: "delete",
    label: t(k.global.misc.delete),
    icon: "pi pi-trash",
    command: () => emits("click:delete"),
  },
]);

const rangeProportionPercent = computed<number>(() => {
  const programmingMilliseconds =
    $date.getDateMilliseconds(props.programmingRange.displayedEndTime) -
    $date.getDateMilliseconds(props.programmingRange.displayedStartTime);
  const displayedMilliseconds =
    $date.getDateMilliseconds(props.displayedEndDate) - $date.getDateMilliseconds(props.displayedStartDate);

  return (programmingMilliseconds / displayedMilliseconds) * 100;
});

const isRealStartExcedingDisplayedStart = computed<boolean>(() => {
  return $date.getDateMilliseconds(props.programmingRange.start) < $date.getDateMilliseconds(props.displayedStartDate);
});

const isRealEndExcedingDisplayedEnd = computed<boolean>(() => {
  const programmingRangeRealEnd = props.programmingRange.end;

  if (!programmingRangeRealEnd) return true;
  return $date.getDateMilliseconds(programmingRangeRealEnd) > $date.getDateMilliseconds(props.displayedEndDate);
});

function toggleProgrammingRangeMenu(event: Event) {
  if (!isEditable.value) return;
  programmingRangeMenu.value?.toggle(event);
}

function getProgrammingRangeActionsLabel(): string {
  return t(k.control.piloting.planning.programmingRangeActions({ name: props.programmingRange.program.name }));
}
</script>

<template>
  <div
    :style="{ width: `${rangeProportionPercent}%` }"
    :class="
      cn('section-programming-range @container', {
        'exceed-start': isRealStartExcedingDisplayedStart,
        'exceed-end': isRealEndExcedingDisplayedEnd,
      })
    "
    :data-test-id="DATA_TEST_IDS.components.sectionProgrammingRange.component"
    :data-programming-range-id="props.programmingRangeId"
  >
    <div
      v-if="props.programmingRange.program.id === DEFAULT_PROGRAM_ID"
      class="default-program program-card gap-1 @[30px]:px-2"
      :data-test-id="DATA_TEST_IDS.components.sectionProgrammingRange.defaultProgram.component"
    >
      <ShieldIcon
        class="shrink-0 @[30px]:block hidden"
        :data-test-id="DATA_TEST_IDS.components.sectionProgrammingRange.defaultProgram.icon"
      />
      <small
        class="font-semibold @[60px]:block hidden truncate"
        :data-test-id="DATA_TEST_IDS.components.sectionProgrammingRange.defaultProgram.text"
        >{{ t(k.control.piloting.planning.defaultProgram) }}</small
      >
    </div>

    <div
      v-else
      :class="
        cn('@[4px]:border-2 program-card group relative', [
          programSystemColor.backgroundColor,
          programSystemColor.borderColor,
        ])
      "
      :data-test-id="DATA_TEST_IDS.components.sectionProgrammingRange.programmingRange.component"
      :data-programming-range-id="props.programmingRangeId"
      :data-program-color="props.programColor"
    >
      <div class="program-name flex flex-1 justify-center items-center @[200px]:pl-[60px] @[200px]:ml-4 min-w-0">
        <small
          :class="cn('truncate font-semibold @[200px]:!px-0 px-2 @[50px]:block hidden', [programSystemColor.textColor])"
          :data-test-id="DATA_TEST_IDS.components.sectionProgrammingRange.programmingRange.text"
          >{{ props.programmingRange.program.name }}</small
        >
      </div>

      <button
        v-if="isEditable"
        type="button"
        aria-haspopup="menu"
        :aria-label="getProgrammingRangeActionsLabel()"
        :aria-controls="programmingRangeMenuId"
        :aria-expanded="isProgrammingRangeMenuVisible"
        :data-programming-range-id="props.programmingRangeId"
        :data-test-id="DATA_TEST_IDS.components.sectionProgrammingRange.programmingRange.actions.menuButton"
        class="programming-range-menu-trigger"
        @click="toggleProgrammingRangeMenu"
      >
        <i :class="cn('programming-range-menu-icon pi pi-ellipsis-v', [programSystemColor.textColor])" />
      </button>

      <div
        v-if="isEditable"
        class="justify-end gap-1 items-center group-hover:opacity-100 group-focus-within:opacity-100 opacity-0 w-[60px] @[200px]:flex @[200px]:mr-4 hidden min-w-0"
        :data-test-id="DATA_TEST_IDS.components.sectionProgrammingRange.programmingRange.actions.component"
      >
        <button
          type="button"
          :aria-label="t(k.global.misc.edit)"
          class="w-[28px] h-[28px] flex items-center justify-center focus-visible:ring-2 focus-visible:ring-brand"
          :data-test-id="DATA_TEST_IDS.components.sectionProgrammingRange.programmingRange.actions.editButton"
          @click="emits('click:edit')"
        >
          <i :class="cn('pi pi-pencil !text-xs', [programSystemColor.textColor])" />
        </button>

        <button
          type="button"
          :aria-label="t(k.global.misc.delete)"
          class="w-[28px] h-[28px] flex items-center justify-center focus-visible:ring-2 focus-visible:ring-brand"
          :data-test-id="DATA_TEST_IDS.components.sectionProgrammingRange.programmingRange.actions.deleteButton"
          @click="emits('click:delete')"
        >
          <i :class="cn('pi pi-trash !text-xs', [programSystemColor.textColor])" />
        </button>
      </div>
    </div>
  </div>

  <Menu
    ref="programmingRangeMenu"
    :id="programmingRangeMenuId"
    :data-test-id="DATA_TEST_IDS.components.sectionProgrammingRange.contextMenu"
    :model="programmingRangeMenuItems"
    :pt="programmingRangeMenuPassThrough"
    popup
    @hide="isProgrammingRangeMenuVisible = false"
    @show="isProgrammingRangeMenuVisible = true"
  />
</template>

<style lang="scss" scoped>
.section-programming-range {
  &.exceed-start {
    .program-card {
      @apply rounded-l-none border-l-0 !important;
    }
  }

  &.exceed-end {
    .program-card {
      @apply rounded-r-none border-r-0 !important;
    }
  }

  .program-card {
    @apply rounded-lg h-[48px] flex items-center justify-center overflow-hidden;
  }

  .programming-range-menu-trigger {
    position: absolute;
    inset: 0;
    z-index: 10;
    display: flex;
    cursor: pointer;
    align-items: center;
    justify-content: center;
    border-radius: 0.25rem;

    &:focus-visible {
      @apply ring-2 ring-brand;
    }
  }

  .programming-range-menu-icon {
    visibility: hidden;
    font-size: 11px !important;
  }

  @container (min-width: 50px) {
    .programming-range-menu-trigger {
      position: static;
      z-index: auto;
      width: 28px;
      height: 28px;
      margin-right: 0.5rem;
      flex-shrink: 0;
    }

    .programming-range-menu-icon {
      visibility: visible;
    }
  }

  @container (min-width: 200px) {
    .programming-range-menu-trigger {
      display: none;
    }
  }
}
</style>
